import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { BsKey, BsEye, BsEyeSlash } from "react-icons/bs";
import {
  API_BASE_URL,
  API_CREDENTIALS_ENDPOINT,
  API_RESET_PASSWORD,
} from "../../constants/constants";
import "../../auth/login/LoginPage.css";
import Seo from '../../seo/Seo';

const VALIDATIONS = [
  { key: "lower", label: "Lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "upper", label: "Uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "digit", label: "Digit", test: (p) => /\d/.test(p) },
  { key: "special", label: "Special character (@$!%*?&)", test: (p) => /[@$!%*?&]/.test(p) },
  { key: "length", label: "8–15 characters", test: (p) => p.length >= 8 && p.length <= 15 },
];

export const ResetPassword = () => {
  const resetPasswordApi = `${API_BASE_URL}${API_CREDENTIALS_ENDPOINT}${API_RESET_PASSWORD}`;
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [isValidToken, setIsValidToken] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const checks = VALIDATIONS.map((v) => ({ ...v, pass: v.test(newPassword) }));
  const isPasswordValid = checks.every((c) => c.pass);
  const doPasswordsMatch = newPassword === confirmPassword;

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const tokenParam = query.get("token");
    if (tokenParam) {
      setToken(tokenParam);
      try {
        const decoded = jwtDecode(tokenParam);
        if (decoded.exp > Date.now() / 1000) {
          setIsValidToken(true);
        } else {
          setErrorMessage("Token has expired. Please request a new password reset.");
        }
      } catch {
        setErrorMessage("Invalid request. Please request a new password reset.");
      }
    } else {
      setErrorMessage("Invalid request. Token is missing.");
    }
  }, [location.search]);

  const handleSubmit = async () => {
    if (!newPassword || !confirmPassword) {
      setErrorMessage("Please fill in both fields.");
      return;
    }
    if (!doPasswordsMatch) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    if (!isPasswordValid) {
      setErrorMessage("Password does not meet all requirements.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        resetPasswordApi,
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        setSuccessMessage("Password has been reset successfully!");
        setErrorMessage("");
        setTimeout(() => navigate("/login"), 3000);
      }
    } catch {
      setErrorMessage("Something went wrong. Please try again later.");
      setSuccessMessage("");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Seo path="/reset-password" />
      <div className="login-card">
        <div className="login-card-inner">
          <div className="login-icon">🔒</div>
          <h1>Reset Password</h1>
          <p className="login-sub">Create a new password for your account</p>

          {errorMessage && <p className="error-msg">{errorMessage}</p>}

          {successMessage ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>✅</div>
              <p style={{ color: "#059669", fontWeight: 600 }}>{successMessage}</p>
              <p style={{ color: "#64748b", fontSize: "0.85rem" }}>Redirecting to login...</p>
            </div>
          ) : isValidToken ? (
            <>
              {/* New Password */}
              <div className="input-group">
                <span className="input-icon"><BsKey /></span>
                <input
                  type={showNewPassword ? "text" : "password"}
                  className={`input-field${!isPasswordValid && newPassword ? " input-error" : ""}`}
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ paddingRight: 42 }}
                />
                <span className="input-icon-right" onClick={() => setShowNewPassword(!showNewPassword)}>
                  {showNewPassword ? <BsEyeSlash /> : <BsEye />}
                </span>
              </div>

              {/* Confirm Password */}
              <div className="input-group">
                <span className="input-icon"><BsKey /></span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className={`input-field${!doPasswordsMatch && confirmPassword ? " input-error" : ""}`}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ paddingRight: 42 }}
                />
                <span className="input-icon-right" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <BsEyeSlash /> : <BsEye />}
                </span>
              </div>

              {/* Inline password requirements */}
              {newPassword && (
                <div style={{ marginBottom: 20 }}>
                  {checks.map((c) => (
                    <div key={c.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", color: c.pass ? "#059669" : "#94a3b8", marginBottom: 2 }}>
                      <span>{c.pass ? "✓" : "○"}</span>
                      <span>{c.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {!doPasswordsMatch && confirmPassword && (
                <p style={{ color: "#ef4444", fontSize: "0.8rem", marginBottom: 16 }}>Passwords do not match</p>
              )}

              <button
                className="btn-submit"
                disabled={!isPasswordValid || !doPasswordsMatch || isLoading || !confirmPassword}
                onClick={handleSubmit}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>

              <p className="login-footer-text">
                Remember your password? <Link to="/login" className="link-accent">Sign in</Link>
              </p>
            </>
          ) : (
            <div style={{ textAlign: "center" }}>
              <p style={{ color: "#64748b" }}>
                <Link to="/login" className="link-accent">Return to login</Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
